import { PlanControlSlot } from '../upgrade/UpgradeExperience';
import MobileSummary from '../components/MobileSummary';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ref } from 'firebase/database';
import {
  FiAlertTriangle,
  FiArrowLeft,
  FiBriefcase,
  FiChevronDown,
  FiChevronRight,
  FiClock,
  FiExternalLink,
  FiMapPin,
  FiPlus,
  FiRefreshCw,
  FiShield,
  FiTarget,
  FiTrendingUp,
  FiX,
  FiZap,
} from 'react-icons/fi';
import { LuRadar } from 'react-icons/lu';
import { db } from '../../firebaseConfig';
import UpgradeGate from '../components/UpgradeGate';
import { createCompanyRadarItem, retryCompanyRadarItem } from '../companyRadarApi';
import { hasPaidAccess } from '../entitlements';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { getCachedFirebaseValue, subscribeCachedValue } from '../firebaseDataCache';
import './CompanyRadarScreen.css';
import { useLanguage } from '../contexts/LanguageContext';

const DETAIL_TABS = [
  { key: 'snapshot', label: 'Summary' },
  { key: 'plan', label: 'Steps' },
  { key: 'intel', label: 'Company' },
];

function normalizeItems(rawItems) {
  return Object.entries(rawItems || {})
    .map(([id, item]) => ({ id, ...(item || {}) }))
    .sort((left, right) => Number(right.updatedAt || right.createdAt || 0) - Number(left.updatedAt || left.createdAt || 0));
}

function cleanList(items) {
  return Array.isArray(items) ? items.filter(Boolean) : [];
}

function getMatch(record) {
  return record?.fit?.bestJobMatch || record?.bestJobMatch || {};
}

function getScore(record) {
  const match = getMatch(record);
  const value = Number(record?.fitScore ?? record?.fit?.fitScore ?? match.matchPercent);
  return Number.isFinite(value) ? Math.max(0, Math.min(100, Math.round(value))) : null;
}

function fitLabel(score, translate) {
  if (score == null) return translate('matchNotReady');
  if (score >= 85) return translate('excellent');
  if (score >= 75) return translate('strong');
  if (score >= 60) return translate('good');
  return translate('may');
}

function formatUpdatedAt(record, language, translate) {
  const value = Number(record?.updatedAt || record?.completedAt || record?.createdAt || 0);
  if (!value) return translate('notUpdated');
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return translate('notUpdated');
  const formatted = new Intl.DateTimeFormat(language === 'ar' ? 'ar-LB' : 'en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  }).format(date);
  return translate('updated').replace('{date}', formatted);
}

function firstBlocker(record) {
  const blocker = cleanList(record?.fit?.blockers || record?.blockers)[0];
  if (!blocker) return '';
  return typeof blocker === 'string' ? blocker : blocker.label || blocker.detail || '';
}

function firstAction(record, translate) {
  const immediate = cleanList(record?.strategy?.nextKeySteps || record?.nextKeySteps);
  if (immediate[0]) return immediate[0];
  const timeline = cleanList(record?.strategy?.timeline);
  return cleanList(timeline[0]?.actions)[0] || translate('openCompanyPlan');
}

function sourceLabel(record, translate) {
  const count = cleanList(record?.sources).length;
  return count ? translate('sources').replace('{count}', count) : translate('sourcesPending');
}

function companyInitial(record) {
  return String(record?.companyName || record?.company?.name || 'C').trim().charAt(0).toUpperCase() || 'C';
}

function taskKey(recordId, phaseIndex, actionIndex) {
  return `${recordId}:${phaseIndex}:${actionIndex}`;
}

function taskStats(record, completedTasks) {
  const timeline = cleanList(record?.strategy?.timeline);
  const keys = timeline.flatMap((phase, phaseIndex) => (
    cleanList(phase?.actions).map((_, actionIndex) => taskKey(record.id, phaseIndex, actionIndex))
  ));
  const complete = keys.filter((key) => completedTasks.has(key)).length;
  return {
    complete,
    total: keys.length,
    percent: keys.length ? Math.round((complete / keys.length) * 100) : 0,
  };
}

function loadCompletedTasks(userId) {
  if (!userId || typeof window === 'undefined') return new Set();
  try {
    const saved = JSON.parse(window.localStorage.getItem(`jobseeqr:company-radar:tasks:${userId}`) || '[]');
    return new Set(Array.isArray(saved) ? saved.filter((item) => typeof item === 'string') : []);
  } catch {
    return new Set();
  }
}

function RadarProgress({ percent, label }) {
  return (
    <div className="js-radar2-progress" aria-label={`${label}: ${percent}%`}>
      <span><i style={{ width: `${percent}%` }} /></span>
      <small>{label}</small>
    </div>
  );
}

function CompanyMark({ record, size = 'default' }) {
  return (
    <span className={`js-radar2-mark is-${size}`} aria-hidden="true">
      {companyInitial(record)}
    </span>
  );
}

function StatusBadge({ record }) {
  const { language, t } = useLanguage();
  const tr = (key) => t(`companyRadarPage.${key}`);
  if (record?.status === 'ready') {
    return (
      <span className="js-radar2-status is-ready">
        <i className="fa-solid fa-check" aria-hidden="true" />
        {tr('ready')}
      </span>
    );
  }
  if (record?.status === 'error') {
    return (
      <span className="js-radar2-status is-error">
        <FiAlertTriangle aria-hidden="true" />
        {tr('tryAgain')}
      </span>
    );
  }
  return (
    <span className="js-radar2-status is-running">
      <FiRefreshCw aria-hidden="true" />
      {language === 'ar' ? tr('working') : record?.stageLabel || tr('working')}
    </span>
  );
}

function Radar2Card({ record, completedTasks, onOpen }) {
  const { language, t } = useLanguage();
  const tr = (key) => t(`companyRadarPage.${key}`);
  const fill = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, String(value)), template);
  const score = getScore(record);
  const match = getMatch(record);
  const stats = taskStats(record, completedTasks);
  const ready = record.status === 'ready';

  return (
    <article className={`js-radar2-card ${ready ? '' : 'is-processing'}`}>
      <button type="button" className="js-radar2-card__open" onClick={onOpen} aria-label={fill(tr('openPlan'), { company: record.companyName || tr('company') })} />
      <header className="js-radar2-card__head">
        <CompanyMark record={record} />
        <div>
          <h2>{record.companyName || tr('company')}</h2>
          <p>{formatUpdatedAt(record, language, tr)}</p>
        </div>
        <StatusBadge record={record} />
      </header>

      {ready ? (
        <>
          <div className="js-radar2-card__match">
            <span className="js-radar2-score is-compact">
              <strong>{score == null ? '—' : `${score}%`}</strong>
              <small>{fitLabel(score, tr)}</small>
            </span>
            <span>
              <small>{tr('bestRole')}</small>
              <strong>{match.title || tr('notReady')}</strong>
            </span>
          </div>

          <dl className="js-radar2-card__signals">
            <div>
              <dt><FiAlertTriangle aria-hidden="true" /> {tr('improve')}</dt>
              <dd>{firstBlocker(record) || tr('noIssue')}</dd>
            </div>
            <div>
              <dt><FiZap aria-hidden="true" /> {tr('nextStep')}</dt>
              <dd>{firstAction(record, tr)}</dd>
            </div>
          </dl>

          <footer className="js-radar2-card__footer">
            <RadarProgress
              percent={stats.percent}
              label={stats.total ? fill(tr('stepsDone'), stats) : tr('stepsReady')}
            />
            <span className="js-radar2-text-link">
              {tr('open')} <FiChevronRight className="js-icon--directional" aria-hidden="true" />
            </span>
          </footer>
        </>
      ) : record.status === 'error' ? (
        <div className="js-radar2-card__message is-error">
          <FiAlertTriangle aria-hidden="true" />
          <p>{record.error || tr('failed')}</p>
        </div>
      ) : (
        <div className="js-radar2-card__message">
          <span className="js-personnel-spinner" aria-hidden="true" />
          <div>
            <p>{record.stageMessage || tr('building')}</p>
            <RadarProgress
              percent={Math.max(4, Math.min(100, Number(record.generationProgress?.progress) || 8))}
              label={tr('making')}
            />
          </div>
        </div>
      )}
    </article>
  );
}

function Radar2AddCompany({ user, onBack, initialCompany = "" }) {
  const { t } = useLanguage();
  const tr = (key) => t(`companyRadarPage.${key}`);
  const [companyName, setCompanyName] = useState(initialCompany);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    if (!companyName.trim()) {
      setError(tr('addError'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await createCompanyRadarItem({
        user,
        companyName: companyName.trim(),
        websiteUrl: websiteUrl.trim(),
      });
      onBack();
    } catch (createError) {
      setError(createError.message || tr('addFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="js-radar2-add" aria-labelledby="radar2-add-title">
      <button type="button" className="js-radar2-back js-secondary-back" onClick={onBack}>
        <FiArrowLeft className="js-icon--directional" aria-hidden="true" />
        {tr('backCompanies')}
      </button>
      <form className="js-radar2-add__card" onSubmit={submit}>
        <div>
          <h1 id="radar2-add-title">{tr('addTitle')}</h1>
          <p>{tr('addCopy')}</p>
        </div>
        <label>
          <span>{tr('companyName')}</span>
          <input
            value={companyName}
            onChange={(event) => setCompanyName(event.target.value)}
            placeholder={tr('companyPlaceholder')}
            autoFocus
          />
        </label>
        <label>
          <span>{tr('website')} <em>{tr('optional')}</em></span>
          <input
            type="url"
            value={websiteUrl}
            onChange={(event) => setWebsiteUrl(event.target.value)}
            placeholder="https://company.com"
          />
        </label>
        {error && <p className="js-radar2-form-error">{error}</p>}
        <div className="js-radar2-add__actions">
          <button type="button" className="js-radar2-button is-secondary" onClick={onBack}>{tr('cancel')}</button>
          <button type="submit" className="js-radar2-button" disabled={saving}>
            {saving ? tr('creating') : tr('create')}
          </button>
        </div>
      </form>
    </section>
  );
}

function Radar2Main({ records, loaded, completedTasks, onAdd, onOpen }) {
  const { t } = useLanguage();
  const tr = (key) => t(`companyRadarPage.${key}`);
  return (
    <section className="js-radar2" aria-labelledby="target-companies-title">
      <header className="js-radar2-main-head">
        <h1 id="target-companies-title">{tr('title')}</h1>
        <button type="button" className="js-radar2-add-button" onClick={onAdd}>
          <FiPlus aria-hidden="true" />
          {tr('addCompany')}
        </button>
      </header>

      {!loaded && (
        <section className="js-radar2-empty" role="status">
          <span className="js-personnel-spinner" aria-hidden="true" />
          <p>{tr('loadingCompanies')}</p>
        </section>
      )}

      {loaded && records.length === 0 && (
        <section className="js-radar2-empty">
          <h2>{tr('emptyTitle')}</h2>
          <p>{tr('emptyCopy')}</p>
          <button type="button" className="js-radar2-add-button" onClick={onAdd}>
            <FiPlus aria-hidden="true" /> {tr('addFirst')}
          </button>
        </section>
      )}

      {records.length > 0 && (
        <div className="js-radar2-grid">
          {records.map((record) => (
            <Radar2Card
              key={record.id}
              record={record}
              completedTasks={completedTasks}
              onOpen={() => onOpen(record.id)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function SnapshotPage({ record, completedTasks, onStartPlan, onViewSources }) {
  const { t } = useLanguage();
  const tr = (key) => t(`companyRadarPage.${key}`);
  const fill = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, String(value)), template);
  const score = getScore(record);
  const match = getMatch(record);
  const fit = record.fit || {};
  const strengths = cleanList(fit.strengths);
  const skills = cleanList(fit.skillsToBuild);
  const blockers = cleanList(fit.blockers || record.blockers);
  const stats = taskStats(record, completedTasks);
  const nextAction = firstAction(record, tr);

  return (
    <div className="js-radar2-snapshot">
      <section className="js-radar2-summary">
        <span className="js-radar2-summary__icon"><LuRadar aria-hidden="true" /></span>
        <div>
          <p className="js-radar2-eyebrow">{tr('yourMatch')}</p>
          <h2>{fill(tr('matchForCompany'), { match: fitLabel(score, tr) })}</h2>
          <MobileSummary>{record.summary || tr('assessmentPending')}</MobileSummary>
          <button type="button" className="js-radar2-evidence-link" onClick={onViewSources}>
            <FiShield aria-hidden="true" />
            {fill(tr('seeSources'), { sources: sourceLabel(record, tr) })}
            <FiChevronRight className="js-icon--directional" aria-hidden="true" />
          </button>
        </div>
      </section>

      <section className="js-radar2-keyline" aria-label={tr('highlights')}>
        <div>
          <FiTarget aria-hidden="true" />
          <span>
            <small>{tr('match')}</small>
            <strong>{score == null ? tr('notReady') : `${score}% · ${fitLabel(score, tr)}`}</strong>
          </span>
        </div>
        <div>
          <FiBriefcase aria-hidden="true" />
          <span>
            <small>{tr('bestRole')}</small>
            <strong>{match.title || tr('rolePending')}</strong>
          </span>
        </div>
        <div>
          <FiClock aria-hidden="true" />
          <span>
            <small>{tr('readiness')}</small>
            <strong>{record.strategy?.readinessProjection || tr('projectionPending')}</strong>
          </span>
        </div>
      </section>

      <div className="js-radar2-snapshot__grid">
        <section className="js-radar2-panel js-radar2-fit-panel">
          <header className="js-radar2-panel__head">
            <div>
              <p className="js-radar2-eyebrow">{tr('why')}</p>
              <h2>{tr('whyMatch')}</h2>
            </div>
            <span className="js-radar2-score">
              <strong>{score == null ? '—' : `${score}%`}</strong>
              <small>{fitLabel(score, tr)}</small>
            </span>
          </header>

          {match.why && <p className="js-radar2-panel__lead">{match.why}</p>}

          <div className="js-radar2-signal-columns">
            <div>
              <h3><i className="fa-solid fa-check" aria-hidden="true" /> {tr('helps')}</h3>
              {strengths.length ? (
                <ul>
                  {strengths.slice(0, 4).map((item) => <li key={item}>{item}</li>)}
                </ul>
              ) : <p>{tr('noStrengths')}</p>}
            </div>
            <div>
              <h3><FiTrendingUp aria-hidden="true" /> {tr('improve')}</h3>
              {(blockers.length || skills.length) ? (
                <ul>
                  {blockers.slice(0, 2).map((item) => (
                    <li key={typeof item === 'string' ? item : `${item.label}-${item.detail}`}>
                      {typeof item === 'string' ? item : item.label || item.detail}
                    </li>
                  ))}
                  {skills.slice(0, Math.max(0, 4 - blockers.slice(0, 2).length)).map((item) => <li key={item}>{item}</li>)}
                </ul>
              ) : <p>{tr('noGaps')}</p>}
            </div>
          </div>
        </section>

        <aside className="js-radar2-action-card">
          <span className="js-radar2-action-card__icon"><FiZap aria-hidden="true" /></span>
          <p className="js-radar2-eyebrow">{tr('nextStep')}</p>
          <h2>{nextAction}</h2>
          {fit.bestEntryPoint && (
            <div className="js-radar2-action-card__route">
              <small>{tr('bestWay')}</small>
              <p>{fit.bestEntryPoint}</p>
              <span>{cleanList(fit.bestEntryLocations || record.bestEntryLocations).join(' · ') || tr('locationPending')}</span>
            </div>
          )}
          <RadarProgress
            percent={stats.percent}
            label={stats.total ? fill(tr('stepsDone'), stats) : tr('stepsReady')}
          />
          <button type="button" className="js-radar2-button" onClick={onStartPlan}>
            {stats.complete ? tr('continueSteps') : tr('startSteps')}
            <FiChevronRight className="js-icon--directional" aria-hidden="true" />
          </button>
        </aside>
      </div>
    </div>
  );
}

function PlanPage({ record, completedTasks, onToggleTask }) {
  const { t } = useLanguage();
  const tr = (key) => t(`companyRadarPage.${key}`);
  const fill = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, String(value)), template);
  const timeline = cleanList(record.strategy?.timeline);
  const stats = taskStats(record, completedTasks);
  const currentPhaseIndex = timeline.findIndex((phase, phaseIndex) => (
    cleanList(phase.actions).some((_, actionIndex) => !completedTasks.has(taskKey(record.id, phaseIndex, actionIndex)))
  ));

  return (
    <div className="js-radar2-plan">
      <section className="js-radar2-plan__head">
        <div>
          <p className="js-radar2-eyebrow">{tr('yourSteps')}</p>
          <h2>{stats.complete ? tr('keepGoing') : tr('startWith')}</h2>
          <p>{record.strategy?.readinessProjection || tr('finishBefore')}</p>
        </div>
        <div className="js-radar2-plan__progress">
          <strong>{stats.percent}%</strong>
          <span>
            <i style={{ width: `${stats.percent}%` }} />
          </span>
          <small>{fill(tr('stepsDone'), stats)}</small>
        </div>
      </section>

      {timeline.length ? (
        <div className="js-radar2-phases">
          {timeline.map((phase, phaseIndex) => {
            const actions = cleanList(phase.actions);
            const phaseComplete = actions.length > 0 && actions.every((_, actionIndex) => (
              completedTasks.has(taskKey(record.id, phaseIndex, actionIndex))
            ));
            const isCurrent = currentPhaseIndex === phaseIndex || (currentPhaseIndex === -1 && phaseIndex === timeline.length - 1);
            return (
              <details
                key={`${phase.timeframe}-${phase.title}`}
                className={`js-radar2-phase ${phaseComplete ? 'is-complete' : ''} ${isCurrent ? 'is-current' : ''}`}
                open={isCurrent || phaseIndex === 0}
              >
                <summary>
                  <span className="js-radar2-phase__number">
                    {phaseComplete ? <i className="fa-solid fa-check" aria-hidden="true" /> : phaseIndex + 1}
                  </span>
                  <span>
                    <small>{phase.timeframe || fill(tr('part'), { number: phaseIndex + 1 })}{isCurrent && !phaseComplete ? ` · ${tr('doNow')}` : ''}</small>
                    <strong>{phase.title || tr('getReady')}</strong>
                  </span>
                  <FiChevronDown aria-hidden="true" />
                </summary>
                <div className="js-radar2-phase__body">
                  {phase.objective && <p>{phase.objective}</p>}
                  <div className="js-radar2-task-list">
                    {actions.map((action, actionIndex) => {
                      const key = taskKey(record.id, phaseIndex, actionIndex);
                      const checked = completedTasks.has(key);
                      return (
                        <label key={key} className={checked ? 'is-complete' : ''}>
                          <input type="checkbox" checked={checked} onChange={() => onToggleTask(key)} />
                          <span className="js-radar2-checkbox">
                            <i className="fa-solid fa-check" aria-hidden="true" />
                          </span>
                          <span>{action}</span>
                        </label>
                      );
                    })}
                  </div>
                  {(phase.proofToBuild || phase.successMetric) && (
                    <dl className="js-radar2-done-when">
                      {phase.proofToBuild && (
                        <div>
                          <dt>{tr('doneWhen')}</dt>
                          <dd>{phase.proofToBuild}</dd>
                        </div>
                      )}
                      {phase.successMetric && (
                        <div>
                          <dt>{tr('goal')}</dt>
                          <dd>{phase.successMetric}</dd>
                        </div>
                      )}
                    </dl>
                  )}
                </div>
              </details>
            );
          })}
        </div>
      ) : (
        <section className="js-radar2-empty is-compact">
          <FiClock aria-hidden="true" />
          <h2>{tr('stepsNotReady')}</h2>
          <p>{tr('checkSoon')}</p>
        </section>
      )}
    </div>
  );
}

function IntelPage({ record, onViewSources }) {
  const { t } = useLanguage();
  const tr = (key) => t(`companyRadarPage.${key}`);
  const fill = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, String(value)), template);
  const company = record.company || {};
  const visa = record.visa || {};
  const locations = cleanList(company.officeLocations);
  const preferredLocations = new Set(cleanList(record.fit?.bestEntryLocations || record.bestEntryLocations).map((item) => item.toLowerCase()));
  const actions = cleanList(visa.actions);
  const notes = cleanList(visa.latestVisaNotes);
  const news = cleanList(record.news);

  return (
    <div className="js-radar2-intel">
      <section className="js-radar2-panel is-wide js-radar2-company-about">
        <div>
          <p className="js-radar2-eyebrow">{tr('about')}</p>
          <h2>{company.name || record.companyName}</h2>
          <p>{company.description || tr('infoPending')}</p>
        </div>
        {company.website && (
          <a href={company.website} target="_blank" rel="noreferrer" className="js-radar2-button is-secondary">
            {tr('companyWebsite')} <FiExternalLink aria-hidden="true" />
          </a>
        )}
      </section>

      <div className="js-radar2-intel__grid">
        <section className="js-radar2-panel">
          <header className="js-radar2-panel__title">
            <span><FiMapPin aria-hidden="true" /></span>
            <div>
              <p className="js-radar2-eyebrow">{tr('locations')}</p>
              <h2>{tr('offices')}</h2>
            </div>
          </header>
          <p className="js-radar2-inline-fact"><strong>{tr('mainOffice')}</strong>{company.headquarters || tr('unknown')}</p>
          <div className="js-radar2-location-list">
            {locations.length ? locations.map((location) => {
              const preferred = Array.from(preferredLocations).some((target) => (
                location.toLowerCase().includes(target) || target.includes(location.toLowerCase())
              ));
              return (
                <span key={location} className={preferred ? 'is-preferred' : ''}>
                  {location}
                  {preferred && <small>{tr('goodLocation')}</small>}
                </span>
              );
            }) : <p>{tr('noOffices')}</p>}
          </div>
        </section>

        <section className="js-radar2-panel">
          <header className="js-radar2-panel__title">
            <span><FiShield aria-hidden="true" /></span>
            <div>
              <p className="js-radar2-eyebrow">{tr('workRights')}</p>
              <h2>{tr('visa')}</h2>
            </div>
          </header>
          <dl className="js-radar2-intel-facts">
            <div>
              <dt>{tr('yourLocation')}</dt>
              <dd>{visa.candidateLocation || record.candidateContext?.current_location || tr('unknown')}</dd>
            </div>
            <div>
              <dt>{tr('sponsorship')}</dt>
              <dd>{visa.sponsorshipSignal || tr('dontKnow')}</dd>
            </div>
          </dl>
          {visa.officeMatch && <p className="js-radar2-note">{visa.officeMatch}</p>}
          <button type="button" className="js-radar2-evidence-link" onClick={onViewSources}>
            <FiShield aria-hidden="true" /> {tr('seeSources').replace('{sources}', tr('sources').replace('{count}', cleanList(record.sources).length))} <FiChevronRight className="js-icon--directional" aria-hidden="true" />
          </button>
        </section>
      </div>

      {(actions.length > 0 || notes.length > 0) && (
        <section className="js-radar2-panel is-wide">
          <header className="js-radar2-panel__title">
            <span><FiZap aria-hidden="true" /></span>
            <div>
              <p className="js-radar2-eyebrow">{tr('beforeApply')}</p>
              <h2>{tr('thingsCheck')}</h2>
            </div>
          </header>
          <div className="js-radar2-intel-lists">
            <div>
              <h3>{tr('whatToDo')}</h3>
              <ul>{actions.map((item) => <li key={item}>{item}</li>)}</ul>
            </div>
            <div>
              <h3>{tr('whatFound')}</h3>
              <ul>{notes.map((item) => <li key={item}>{item}</li>)}</ul>
            </div>
          </div>
        </section>
      )}

      {news.length > 0 && (
        <section className="js-radar2-panel is-wide">
          <header className="js-radar2-panel__title">
            <span><FiTrendingUp aria-hidden="true" /></span>
            <div>
              <p className="js-radar2-eyebrow">{tr('news')}</p>
              <h2>{tr('newsKnow')}</h2>
            </div>
          </header>
          <div className="js-radar2-news">
            {news.map((item) => (
              <article key={`${item.headline}-${item.sourceIndex}`}>
                <strong>{item.headline}</strong>
                <p>{item.whyItMatters}</p>
                {item.sourceIndex ? <small>{fill(tr('sourceNumber'), { number: item.sourceIndex })}</small> : null}
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function SourcesDrawer({ record, onClose }) {
  const { language, t } = useLanguage();
  const tr = (key) => t(`companyRadarPage.${key}`);
  const sources = cleanList(record?.sources);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="js-radar2-drawer-shell" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <aside className="js-radar2-drawer" role="dialog" aria-modal="true" aria-labelledby="radar2-sources-title">
        <header>
          <div>
            <p className="js-radar2-eyebrow">{tr('source')}</p>
            <h2 id="radar2-sources-title">{tr('whereFrom')}</h2>
            <p>{sourceLabel(record, tr)} · {formatUpdatedAt(record, language, tr)}</p>
          </div>
          <button type="button" onClick={onClose} aria-label={tr('closeSources')}>
            <FiX aria-hidden="true" />
          </button>
        </header>
        <div className="js-radar2-drawer__body">
          {sources.length ? sources.map((source, index) => (
            <a key={`${source.index || index}-${source.url}`} href={source.url} target="_blank" rel="noreferrer">
              <span>{source.index || index + 1}</span>
              <div>
                <strong>{source.title || source.domain || tr('source')}</strong>
                <small>{[source.domain, source.publication_date].filter(Boolean).join(' · ')}</small>
                {source.excerpt && <p>{source.excerpt}</p>}
              </div>
              <FiExternalLink aria-hidden="true" />
            </a>
          )) : (
            <section className="js-radar2-empty is-compact">
              <FiShield aria-hidden="true" />
              <h2>{tr('noSources')}</h2>
              <p>{tr('sourcesLater')}</p>
            </section>
          )}
        </div>
      </aside>
    </div>
  );
}

function Radar2Detail({ record, user, completedTasks, onToggleTask }) {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const tr = (key) => t(`companyRadarPage.${key}`);
  const [activeTab, setActiveTab] = useState('snapshot');
  const [showSources, setShowSources] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState('');

  useEffect(() => {
    setActiveTab('snapshot');
    setShowSources(false);
  }, [record?.id]);

  const retry = async () => {
    if (!record?.id) return;
    setRetrying(true);
    setRetryError('');
    try {
      await retryCompanyRadarItem({ user, radarId: record.id });
    } catch (error) {
      setRetryError(error.message || tr('retryCompany'));
    } finally {
      setRetrying(false);
    }
  };

  const ready = record?.status === 'ready';

  return (
    <section className="js-radar2-detail" aria-label={tr('targetPlan')}>
      <header className="js-radar2-detail__head">
        <button type="button" className="js-radar2-back js-secondary-back" onClick={() => navigate('/company-radar')}>
          <FiArrowLeft className="js-icon--directional" aria-hidden="true" /> {tr('backCompanies')}
        </button>
        <div className="js-radar2-detail__identity">
          <CompanyMark record={record || { companyName: tr('company') }} size="large" />
          <div>
            <h1>{record?.companyName || tr('targetCompany')}</h1>
            <p>{ready ? `${formatUpdatedAt(record, language, tr)} · ${sourceLabel(record, tr)}` : language === 'ar' ? tr('loadingRadar') : record?.stageLabel || tr('loadingRadar')}</p>
          </div>
        </div>
        <div className="js-focus-plan-actions"><PlanControlSlot />{ready ? (
          <button
            type="button"
            className="js-radar2-sources-button"
            aria-label={`${tr('open')} ${sourceLabel(record, tr)}`}
            onClick={() => setShowSources(true)}
          >
            <FiShield aria-hidden="true" />
              <span>{tr('source')}</span>
            <b>{cleanList(record.sources).length}</b>
          </button>
        ) : <span />}</div>
      </header>

      {ready && (
        <nav className="js-radar2-detail__tabs" role="tablist" aria-label={tr('planSections')}>
          {DETAIL_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={activeTab === tab.key ? 'is-active' : ''}
              onClick={() => setActiveTab(tab.key)}
            >
              {tr(`tabs.${tab.key}`)}
            </button>
          ))}
        </nav>
      )}

      <main key={activeTab} className="js-radar2-detail__body">
        {!record && (
          <section className="js-radar2-empty">
            <span className="js-personnel-spinner" aria-hidden="true" />
            <p>{tr('loadingPlan')}</p>
          </section>
        )}

        {record && !ready && record.status !== 'error' && (
          <section className="js-radar2-empty">
            <span className="js-personnel-spinner" aria-hidden="true" />
            <h2>{language === 'ar' ? tr('researching') : record.stageLabel || tr('researching')}</h2>
            <p>{language === 'ar' ? tr('building') : record.stageMessage || tr('building')}</p>
            <RadarProgress
              percent={Math.max(4, Math.min(100, Number(record.generationProgress?.progress) || 8))}
              label={tr('making')}
            />
          </section>
        )}

        {record?.status === 'error' && (
          <section className="js-radar2-empty">
            <span className="js-radar2-empty__icon is-error"><FiAlertTriangle aria-hidden="true" /></span>
            <h2>{tr('planFailed')}</h2>
            <p>{record.error || tr('pleaseRetry')}</p>
            <button type="button" className="js-radar2-button" onClick={retry} disabled={retrying}>
              <FiRefreshCw aria-hidden="true" />
              {retrying ? tr('trying') : tr('tryAgain')}
            </button>
            {retryError && <p className="js-radar2-form-error">{retryError}</p>}
          </section>
        )}

        {ready && activeTab === 'snapshot' && (
          <SnapshotPage
            record={record}
            completedTasks={completedTasks}
            onStartPlan={() => setActiveTab('plan')}
            onViewSources={() => setShowSources(true)}
          />
        )}
        {ready && activeTab === 'plan' && (
          <PlanPage record={record} completedTasks={completedTasks} onToggleTask={onToggleTask} />
        )}
        {ready && activeTab === 'intel' && (
          <IntelPage record={record} onViewSources={() => setShowSources(true)} />
        )}
      </main>

      {showSources && <SourcesDrawer record={record} onClose={() => setShowSources(false)} />}
    </section>
  );
}

function CompanyRadarScreen({ user }) {
  const location = useLocation();
  const prefillHandled = useRef(false);
  const { entitlements } = useEntitlements();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const radarPath = user?.uid ? `people/${user.uid}/companyRadar` : '';
  const cachedRadar = radarPath ? getCachedFirebaseValue(radarPath) : undefined;
  const [records, setRecords] = useState(() => normalizeItems(cachedRadar || {}));
  const [loaded, setLoaded] = useState(!user?.uid || cachedRadar !== undefined);
  const [mode, setMode] = useState('main');
  const [completedTasks, setCompletedTasks] = useState(() => loadCompletedTasks(user?.uid));
  const paid = hasPaidAccess(entitlements);
  const visibleRecords = useMemo(() => {
    if (paid || records.length <= 1) return records;
    const firstFreeCompany = [...records]
      .sort((left, right) => Number(left.createdAt || 0) - Number(right.createdAt || 0))[0];
    return firstFreeCompany ? [firstFreeCompany] : [];
  }, [paid, records]);

  useEffect(() => {
    if (!user?.uid) {
      setRecords([]);
      setLoaded(true);
      return undefined;
    }
    if (getCachedFirebaseValue(radarPath) === undefined) setLoaded(false);
    return subscribeCachedValue(ref(db, radarPath), radarPath, (snapshot) => {
      setRecords(normalizeItems(snapshot.val() || {}));
      setLoaded(true);
    });
  }, [radarPath, user?.uid]);

  useEffect(() => {
    if (!user?.uid || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        `jobseeqr:company-radar:tasks:${user.uid}`,
        JSON.stringify(Array.from(completedTasks)),
      );
    } catch {
      // Keep the prototype usable when storage is unavailable.
    }
  }, [completedTasks, user?.uid]);

  const selectedRecord = useMemo(
    () => visibleRecords.find((record) => record.id === companyId) || null,
    [companyId, visibleRecords],
  );

  useEffect(() => {
    const company = String(location.state?.companyName || '').trim();
    if (!loaded || !entitlements || !company || prefillHandled.current) return;
    prefillHandled.current = true;
    const existing = visibleRecords.find((record) => record.companyName?.toLowerCase() === company.toLowerCase());
    if (existing) navigate(`/company-radar/${encodeURIComponent(existing.id)}`, { replace: true });
    else setMode(paid || records.length === 0 ? 'add' : 'upgrade');
  }, [loaded, entitlements, location.state, visibleRecords, navigate, paid, records.length]);

  const toggleTask = (key) => {
    setCompletedTasks((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (companyId && !selectedRecord && !paid && records.some((record) => record.id === companyId)) {
    return (
      <UpgradeGate
        user={user}
        context="company_radar"
      />
    );
  }

  if (companyId) {
    return (
      <Radar2Detail
        record={selectedRecord}
        user={user}
        completedTasks={completedTasks}
        onToggleTask={toggleTask}
      />
    );
  }

  if (mode === 'add') {
    return <Radar2AddCompany user={user} initialCompany={location.state?.companyName} onBack={() => setMode('main')} />;
  }

  if (mode === 'upgrade') {
    if (paid) return <Radar2AddCompany user={user} initialCompany={location.state?.companyName} onBack={() => setMode('main')} />;
    return (
      <section className="js-radar2-upgrade">
        <button type="button" className="js-radar2-back js-secondary-back" onClick={() => setMode('main')}>
          <FiArrowLeft className="js-icon--directional" aria-hidden="true" /> Back to Company Radar
        </button>
        <UpgradeGate
          user={user}
          context="company_radar"
        />
      </section>
    );
  }

  return (
    <Radar2Main
      records={visibleRecords}
      loaded={loaded}
      completedTasks={completedTasks}
      onAdd={() => setMode(!paid && records.length >= 1 ? 'upgrade' : 'add')}
      onOpen={(id) => navigate(`/company-radar/${id}`)}
    />
  );
}

export default CompanyRadarScreen;
